import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Linkedin, MapPin, Users, Rocket, ChevronDown } from 'lucide-react';
import SEO from '../components/SEO';

const AboutPage = () => {
  const [scrollY, setScrollY] = useState(0);
  const [vh, setVh] = useState(800);

  useEffect(() => {
    setVh(window.innerHeight);
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrollY(window.scrollY);
          ticking = false;
        });
        ticking = true;
      }
    };
    const onResize = () => setVh(window.innerHeight);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // --- Animation math ---
  const clamp = (v) => Math.max(0, Math.min(1, v));
  const r = (progress, start, end) => clamp((progress - start) / (end - start));
  const lerp = (a, b, t) => a + (b - a) * t;

  // Scene progress: each container = 200vh, sticky = 100vh, scroll range = 100vh
  const SVH = 2;
  const sd = (SVH - 1) * vh;
  const sp = (idx) => clamp((scrollY - idx * SVH * vh) / sd);

  const SCENES = 5;
  const animEnd = SCENES * SVH * vh;
  const activeScene = Math.min(SCENES - 1, Math.floor(scrollY / (SVH * vh)));
  const inAnimatedZone = scrollY < animEnd;

  return (
    <div className="bg-slate-950 text-white" data-testid="about-page">
      <SEO
        title="About OwlWorkLabs | How We Build Ventures"
        description="Discover how OwlWorkLabs builds ventures from identifying friction to scaling solutions. A UK-based venture studio with a systematic approach to building intelligent systems."
        canonical="https://www.owlworklabs.com/about"
      />

      {/* Fixed Header — dark theme */}
      <header className="fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 pt-3 sm:pt-4" data-testid="about-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between rounded-2xl bg-slate-900/80 backdrop-blur-xl border border-slate-700/50 shadow-xl">
          <Link to="/" className="flex items-center gap-3" data-testid="about-header-logo">
            <img src="/logo.png" alt="OwlWorkLabs Logo" className="w-10 h-10 sm:w-12 sm:h-12 object-contain" />
            <span className="text-lg sm:text-xl font-bold text-white">OwlWorkLabs</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-xs sm:text-sm font-medium text-slate-300 hover:text-white transition-all px-3 sm:px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-slate-700/50" data-testid="about-back-btn">
            <ArrowLeft className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Back to Home</span>
            <span className="sm:hidden">Back</span>
          </Link>
        </div>
      </header>

      {/* Progress dots — right side */}
      {inAnimatedZone && (
        <div className="fixed right-3 sm:right-6 top-1/2 -translate-y-1/2 z-40 flex flex-col items-center gap-2.5" data-testid="scroll-progress-dots">
          <div className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${activeScene === 0 ? 'bg-white scale-150' : 'bg-white/20'}`} />
          <div className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${activeScene === 1 ? 'bg-white scale-150' : 'bg-white/20'}`} />
          <div className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${activeScene === 2 ? 'bg-white scale-150' : 'bg-white/20'}`} />
          <div className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${activeScene === 3 ? 'bg-white scale-150' : 'bg-white/20'}`} />
          <div className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${activeScene === 4 ? 'bg-white scale-150' : 'bg-white/20'}`} />
        </div>
      )}

      {/* Scroll hint */}
      <div
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex flex-col items-center text-slate-600 transition-opacity duration-500"
        style={{ opacity: scrollY < vh * 0.3 ? 1 : 0, pointerEvents: 'none' }}
      >
        <span className="text-[10px] tracking-widest uppercase mb-1">Scroll</span>
        <ChevronDown className="w-4 h-4 animate-bounce" />
      </div>

      {/* ============================== */}
      {/* SCENE 0 — OPENING             */}
      {/* ============================== */}
      <div style={{ height: `${SVH * 100}vh` }}>
        <div className="sticky top-0 h-screen flex items-center justify-center overflow-hidden">
          {/* Subtle grid */}
          <div className="absolute inset-0 bg-[linear-gradient(rgba(148,163,184,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.03)_1px,transparent_1px)] bg-[size:48px_48px]" />

          {/* Expanding orb */}
          <div
            className="absolute w-[500px] h-[500px] rounded-full bg-gradient-to-br from-blue-600/8 to-purple-600/8 blur-[100px]"
            style={{ transform: `scale(${0.4 + sp(0) * 1.2})`, opacity: 0.4 }}
          />

          <div className="relative z-10 text-center px-6 max-w-4xl">
            <span
              className="text-xs sm:text-sm text-slate-600 tracking-[0.35em] uppercase block"
              style={{
                opacity: Math.max(0.8, r(sp(0), 0, 0.15)),
                transform: `translateY(${lerp(8, 0, Math.max(0.5, r(sp(0), 0, 0.15)))}px)`
              }}
            >
              OwlWorkLabs presents
            </span>

            <h1
              className="text-5xl sm:text-7xl md:text-[6rem] font-black mt-4 leading-[0.95] tracking-tight"
              style={{
                opacity: Math.max(0.7, r(sp(0), 0, 0.3)),
                transform: `translateY(${lerp(15, 0, Math.max(0.3, r(sp(0), 0, 0.3)))}px)`
              }}
            >
              How We Build
              <br />
              <span className="text-slate-600">Ventures</span>
            </h1>

            <p
              className="text-sm sm:text-lg text-slate-500 mt-5 sm:mt-8 max-w-xl mx-auto leading-relaxed"
              style={{
                opacity: r(sp(0), 0.15, 0.55),
                transform: `translateY(${lerp(20, 0, r(sp(0), 0.15, 0.55))}px)`
              }}
            >
              From spotting friction to scaling solutions — a look inside the process behind every OwlWork venture.
            </p>
          </div>
        </div>
      </div>

      {/* ============================== */}
      {/* SCENE 1 — DISCOVER            */}
      {/* ============================== */}
      <div style={{ height: `${SVH * 100}vh` }}>
        <div className="sticky top-0 h-screen flex items-center overflow-hidden">
          {/* Concentric rings */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
            <div
              className="absolute rounded-full border border-white/[0.04]"
              style={{
                width: 200, height: 200, left: -100, top: -100,
                transform: `scale(${r(sp(1), 0, 0.3)})`,
                opacity: r(sp(1), 0, 0.3)
              }}
            />
            <div
              className="absolute rounded-full border border-white/[0.03]"
              style={{
                width: 350, height: 350, left: -175, top: -175,
                transform: `scale(${r(sp(1), 0.1, 0.4)})`,
                opacity: r(sp(1), 0.1, 0.4) * 0.7
              }}
            />
            <div
              className="absolute rounded-full border border-white/[0.02]"
              style={{
                width: 500, height: 500, left: -250, top: -250,
                transform: `scale(${r(sp(1), 0.2, 0.5)})`,
                opacity: r(sp(1), 0.2, 0.5) * 0.5
              }}
            />
          </div>

          <div className="relative z-10 max-w-6xl mx-auto px-6 w-full">
            <div className="flex flex-col lg:flex-row items-center gap-6 lg:gap-16">
              {/* Number */}
              <div
                className="flex-shrink-0"
                style={{
                  opacity: r(sp(1), 0, 0.25),
                  transform: `translateY(${lerp(30, 0, r(sp(1), 0, 0.25))}px)`
                }}
              >
                <span className="text-[100px] sm:text-[140px] lg:text-[180px] font-black text-white/[0.04] leading-none select-none">01</span>
              </div>

              {/* Content */}
              <div className="flex-1 text-center lg:text-left">
                <span
                  className="text-xs sm:text-sm text-slate-600 tracking-[0.2em] uppercase block"
                  style={{
                    opacity: r(sp(1), 0.05, 0.3),
                    transform: `translateY(${lerp(15, 0, r(sp(1), 0.05, 0.3))}px)`
                  }}
                >
                  Discover
                </span>
                <h2
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 leading-tight"
                  style={{
                    opacity: r(sp(1), 0.12, 0.45),
                    transform: `translateY(${lerp(30, 0, r(sp(1), 0.12, 0.45))}px)`
                  }}
                >
                  We find the questions
                  <br />
                  <span className="text-slate-600">nobody is asking</span>
                </h2>
                <p
                  className="text-sm sm:text-base text-slate-500 mt-4 sm:mt-6 max-w-lg leading-relaxed mx-auto lg:mx-0"
                  style={{
                    opacity: r(sp(1), 0.3, 0.6),
                    transform: `translateY(${lerp(20, 0, r(sp(1), 0.3, 0.6))}px)`
                  }}
                >
                  We look at how people travel, collaborate, dress, and work — then identify where friction hides in plain sight. The best opportunities are the ones everyone has accepted as normal.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============================== */}
      {/* SCENE 2 — DESIGN              */}
      {/* ============================== */}
      <div style={{ height: `${SVH * 100}vh` }}>
        <div className="sticky top-0 h-screen flex items-center overflow-hidden">
          {/* Grid lines */}
          <div className="absolute inset-0 pointer-events-none" style={{ opacity: r(sp(2), 0.1, 0.5) * 0.15 }}>
            <div className="absolute top-1/4 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white to-transparent" />
            <div className="absolute top-2/4 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white to-transparent" />
            <div className="absolute top-3/4 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white to-transparent" />
            <div className="absolute left-1/4 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-white to-transparent" />
            <div className="absolute left-2/4 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-white to-transparent" />
            <div className="absolute left-3/4 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-white to-transparent" />
          </div>

          <div className="relative z-10 max-w-6xl mx-auto px-6 w-full">
            <div className="flex flex-col lg:flex-row items-center gap-6 lg:gap-16">
              <div
                className="flex-shrink-0"
                style={{
                  opacity: r(sp(2), 0, 0.25),
                  transform: `translateY(${lerp(30, 0, r(sp(2), 0, 0.25))}px)`
                }}
              >
                <span className="text-[100px] sm:text-[140px] lg:text-[180px] font-black text-white/[0.04] leading-none select-none">02</span>
              </div>

              <div className="flex-1 text-center lg:text-left">
                <span
                  className="text-xs sm:text-sm text-slate-600 tracking-[0.2em] uppercase block"
                  style={{
                    opacity: r(sp(2), 0.05, 0.3),
                    transform: `translateY(${lerp(15, 0, r(sp(2), 0.05, 0.3))}px)`
                  }}
                >
                  Design
                </span>
                <h2
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 leading-tight"
                  style={{
                    opacity: r(sp(2), 0.12, 0.45),
                    transform: `translateY(${lerp(30, 0, r(sp(2), 0.12, 0.45))}px)`
                  }}
                >
                  We architect systems,
                  <br />
                  <span className="text-slate-600">not features</span>
                </h2>
                <p
                  className="text-sm sm:text-base text-slate-500 mt-4 sm:mt-6 max-w-lg leading-relaxed mx-auto lg:mx-0"
                  style={{
                    opacity: r(sp(2), 0.3, 0.6),
                    transform: `translateY(${lerp(20, 0, r(sp(2), 0.3, 0.6))}px)`
                  }}
                >
                  Every venture gets a full architecture before a single line of code. Business model, unit economics, user flows, technology stack — we map the entire system so every component has a purpose and every connection is intentional.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============================== */}
      {/* SCENE 3 — BUILD               */}
      {/* ============================== */}
      <div style={{ height: `${SVH * 100}vh` }}>
        <div className="sticky top-0 h-screen flex items-center overflow-hidden">
          {/* Animated progress blocks */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none flex gap-2">
            <div
              className="w-2 bg-white/[0.06] rounded-full"
              style={{ height: `${r(sp(3), 0, 0.4) * 120}px`, transition: 'height 0.1s' }}
            />
            <div
              className="w-2 bg-white/[0.05] rounded-full"
              style={{ height: `${r(sp(3), 0.05, 0.45) * 180}px`, transition: 'height 0.1s' }}
            />
            <div
              className="w-2 bg-white/[0.06] rounded-full"
              style={{ height: `${r(sp(3), 0.1, 0.5) * 100}px`, transition: 'height 0.1s' }}
            />
            <div
              className="w-2 bg-white/[0.05] rounded-full"
              style={{ height: `${r(sp(3), 0.08, 0.48) * 160}px`, transition: 'height 0.1s' }}
            />
            <div
              className="w-2 bg-white/[0.06] rounded-full"
              style={{ height: `${r(sp(3), 0.03, 0.43) * 140}px`, transition: 'height 0.1s' }}
            />
          </div>

          <div className="relative z-10 max-w-6xl mx-auto px-6 w-full">
            <div className="flex flex-col lg:flex-row items-center gap-6 lg:gap-16">
              <div
                className="flex-shrink-0"
                style={{
                  opacity: r(sp(3), 0, 0.25),
                  transform: `translateY(${lerp(30, 0, r(sp(3), 0, 0.25))}px)`
                }}
              >
                <span className="text-[100px] sm:text-[140px] lg:text-[180px] font-black text-white/[0.04] leading-none select-none">03</span>
              </div>

              <div className="flex-1 text-center lg:text-left">
                <span
                  className="text-xs sm:text-sm text-slate-600 tracking-[0.2em] uppercase block"
                  style={{
                    opacity: r(sp(3), 0.05, 0.3),
                    transform: `translateY(${lerp(15, 0, r(sp(3), 0.05, 0.3))}px)`
                  }}
                >
                  Build
                </span>
                <h2
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 leading-tight"
                  style={{
                    opacity: r(sp(3), 0.12, 0.45),
                    transform: `translateY(${lerp(30, 0, r(sp(3), 0.12, 0.45))}px)`
                  }}
                >
                  Small teams. Fast cycles.
                  <br />
                  <span className="text-slate-600">Real users from day one.</span>
                </h2>
                <p
                  className="text-sm sm:text-base text-slate-500 mt-4 sm:mt-6 max-w-lg leading-relaxed mx-auto lg:mx-0"
                  style={{
                    opacity: r(sp(3), 0.3, 0.6),
                    transform: `translateY(${lerp(20, 0, r(sp(3), 0.3, 0.6))}px)`
                  }}
                >
                  We don't build in isolation. Every venture ships its first version to real users as fast as possible. Feedback drives decisions — not assumptions. Lean teams, clear ownership, rapid iteration.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============================== */}
      {/* SCENE 4 — SCALE               */}
      {/* ============================== */}
      <div style={{ height: `${SVH * 100}vh` }}>
        <div className="sticky top-0 h-screen flex items-center overflow-hidden">
          {/* Ripple rings */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
            <div
              className="absolute rounded-full border border-white/[0.03]"
              style={{
                width: 100, height: 100, left: -50, top: -50,
                transform: `scale(${1 + r(sp(4), 0, 0.5) * 4})`,
                opacity: (1 - r(sp(4), 0, 0.5)) * 0.4
              }}
            />
            <div
              className="absolute rounded-full border border-white/[0.03]"
              style={{
                width: 100, height: 100, left: -50, top: -50,
                transform: `scale(${1 + r(sp(4), 0.1, 0.6) * 4})`,
                opacity: (1 - r(sp(4), 0.1, 0.6)) * 0.3
              }}
            />
            <div
              className="absolute rounded-full border border-white/[0.03]"
              style={{
                width: 100, height: 100, left: -50, top: -50,
                transform: `scale(${1 + r(sp(4), 0.2, 0.7) * 4})`,
                opacity: (1 - r(sp(4), 0.2, 0.7)) * 0.2
              }}
            />
          </div>

          <div className="relative z-10 max-w-6xl mx-auto px-6 w-full">
            <div className="flex flex-col lg:flex-row items-center gap-6 lg:gap-16">
              <div
                className="flex-shrink-0"
                style={{
                  opacity: r(sp(4), 0, 0.25),
                  transform: `translateY(${lerp(30, 0, r(sp(4), 0, 0.25))}px)`
                }}
              >
                <span className="text-[100px] sm:text-[140px] lg:text-[180px] font-black text-white/[0.04] leading-none select-none">04</span>
              </div>

              <div className="flex-1 text-center lg:text-left">
                <span
                  className="text-xs sm:text-sm text-slate-600 tracking-[0.2em] uppercase block"
                  style={{
                    opacity: r(sp(4), 0.05, 0.3),
                    transform: `translateY(${lerp(15, 0, r(sp(4), 0.05, 0.3))}px)`
                  }}
                >
                  Scale
                </span>
                <h2
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 leading-tight"
                  style={{
                    opacity: r(sp(4), 0.12, 0.45),
                    transform: `translateY(${lerp(30, 0, r(sp(4), 0.12, 0.45))}px)`
                  }}
                >
                  What works gets fuel.
                  <br />
                  <span className="text-slate-600">What doesn't gets cut.</span>
                </h2>
                <p
                  className="text-sm sm:text-base text-slate-500 mt-4 sm:mt-6 max-w-lg leading-relaxed mx-auto lg:mx-0"
                  style={{
                    opacity: r(sp(4), 0.3, 0.6),
                    transform: `translateY(${lerp(20, 0, r(sp(4), 0.3, 0.6))}px)`
                  }}
                >
                  We measure everything — user engagement, unit economics, retention. Ventures that prove product-market fit get full resources. Those that don't get pivoted or shut down. No sentimentality, just signal.
                </p>

                {/* Ventures count */}
                <div
                  className="mt-8 flex flex-wrap gap-6 justify-center lg:justify-start"
                  style={{
                    opacity: r(sp(4), 0.5, 0.8),
                    transform: `translateY(${lerp(15, 0, r(sp(4), 0.5, 0.8))}px)`
                  }}
                >
                  <div>
                    <span className="text-3xl sm:text-4xl font-black text-white">4</span>
                    <p className="text-xs text-slate-600 mt-1">Active Ventures</p>
                  </div>
                  <div className="w-px h-12 bg-slate-800" />
                  <div>
                    <span className="text-3xl sm:text-4xl font-black text-white">1</span>
                    <p className="text-xs text-slate-600 mt-1">Philosophy</p>
                  </div>
                  <div className="w-px h-12 bg-slate-800" />
                  <div>
                    <span className="text-3xl sm:text-4xl font-black text-white">0</span>
                    <p className="text-xs text-slate-600 mt-1">Wasted Motion</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============================== */}
      {/* THE STORY — brief founder note */}
      {/* ============================== */}
      <section className="py-20 sm:py-28 bg-slate-950 border-t border-slate-800/50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="text-xs text-slate-600 tracking-[0.3em] uppercase">The Origin</span>
          <h2 className="text-3xl sm:text-4xl font-bold mt-4 mb-8 text-white">
            Built from experience, not theory
          </h2>
          <p className="text-base sm:text-lg text-slate-400 leading-relaxed max-w-3xl mx-auto">
            OwlWorkLabs was founded by <strong className="text-slate-300">Khan Mujawar</strong> — someone who learned business from the inside out. From managing operations at startups in India to studying International Business in London, every experience shaped the same conviction: businesses fail from inefficient execution, not bad ideas. OwlWorkLabs exists to fix that — one venture at a time.
          </p>
          <div className="flex items-center justify-center gap-6 mt-8 text-sm text-slate-500">
            <span className="flex items-center gap-2"><MapPin className="w-4 h-4" /> London, UK</span>
            <span className="flex items-center gap-2"><Rocket className="w-4 h-4" /> Est. 2024</span>
          </div>
        </div>
      </section>

      {/* ============================== */}
      {/* COLLABORATIVE MODEL            */}
      {/* ============================== */}
      <section className="py-20 sm:py-28 bg-slate-900/50 relative overflow-hidden">
        <div className="absolute top-20 right-1/4 w-72 h-72 bg-blue-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-1/4 w-64 h-64 bg-purple-500/5 rounded-full blur-3xl" />

        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="text-center mb-14">
            <span className="text-xs text-slate-600 tracking-[0.3em] uppercase">How We Work</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-4 text-white">Built for collaboration</h2>
            <p className="text-base text-slate-500 mt-4 max-w-2xl mx-auto">
              We don't believe in one-size-fits-all teams. Each venture has different friction, so we build modular, agile teams tailored to specific needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* How We Work card */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-slate-700/50">
              <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center mb-5">
                <Users className="w-6 h-6 text-slate-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Our Model</h3>
              <ul className="space-y-3 text-slate-400 text-sm">
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-500 mt-1.5 flex-shrink-0" />
                  <span>Core team handles strategy, product direction, and key decisions</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-500 mt-1.5 flex-shrink-0" />
                  <span>Specialist collaborators join based on project requirements</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-500 mt-1.5 flex-shrink-0" />
                  <span>Resources flow between ventures — accelerating what works</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-500 mt-1.5 flex-shrink-0" />
                  <span>Focus on ROI and measurable outcomes, not just activity</span>
                </li>
              </ul>
            </div>

            {/* Collaborators CTA card */}
            <div className="bg-gradient-to-br from-white/[0.08] to-white/[0.03] backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-slate-700/50">
              <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center mb-5">
                <Rocket className="w-6 h-6 text-slate-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Join as a collaborator</h3>
              <p className="text-slate-400 text-sm mb-5 leading-relaxed">
                We're always looking for talented people who want to build products that matter. If you're skilled in any of these areas:
              </p>
              <div className="flex flex-wrap gap-2 mb-6">
                <span className="px-2.5 py-1 rounded-full bg-white/5 text-xs text-slate-400 border border-slate-700/50">Development</span>
                <span className="px-2.5 py-1 rounded-full bg-white/5 text-xs text-slate-400 border border-slate-700/50">Design</span>
                <span className="px-2.5 py-1 rounded-full bg-white/5 text-xs text-slate-400 border border-slate-700/50">Marketing</span>
                <span className="px-2.5 py-1 rounded-full bg-white/5 text-xs text-slate-400 border border-slate-700/50">Operations</span>
                <span className="px-2.5 py-1 rounded-full bg-white/5 text-xs text-slate-400 border border-slate-700/50">Strategy</span>
                <span className="px-2.5 py-1 rounded-full bg-white/5 text-xs text-slate-400 border border-slate-700/50">Finance</span>
              </div>
              <a
                href="https://tally.so/r/q4BLR9"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-slate-900 text-sm font-semibold rounded-full hover:bg-slate-200 transition-colors"
                data-testid="about-collab-cta"
              >
                <Mail className="w-4 h-4" />
                Get in touch
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ============================== */}
      {/* CTA + FOOTER                   */}
      {/* ============================== */}
      <section className="py-20 sm:py-28 bg-slate-950 border-t border-slate-800/50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-5 text-white">Let's build something together</h2>
          <p className="text-base sm:text-lg text-slate-500 mb-10 max-w-2xl mx-auto">
            Whether you're interested in our ventures, consulting services, partnerships, or joining as a collaborator — we'd love to connect.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-sm">
            <a href="mailto:owlworklabs@gmail.com" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors" data-testid="about-email-link">
              <Mail className="w-5 h-5" />
              owlworklabs@gmail.com
            </a>
            <a href="https://www.linkedin.com/company/owl-work-labs" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors" data-testid="about-linkedin-link">
              <Linkedin className="w-5 h-5" />
              LinkedIn
            </a>
            <span className="flex items-center gap-2 text-slate-600">
              <MapPin className="w-5 h-5" />
              London, UK
            </span>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 mt-16 pt-8 border-t border-slate-800/50 text-center text-slate-700 text-xs">
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mb-3">
            <Link to="/privacy" className="text-slate-500 hover:text-slate-300 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="text-slate-500 hover:text-slate-300 transition-colors">Terms &amp; Conditions</Link>
          </div>
          <p>&copy; {new Date().getFullYear()} OwlWorkLabs Ltd. All rights reserved. · Company No. 16713122</p>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
